// NEW_FILE_JUSTIFIED: Knowledge base texts tab — inline form + entries table, no existing equivalent
import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Type, Loader2, Trash2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';
import { useTextEntryForm } from '@/hooks/useTextEntryForm';
import type { IKBTextsTabProps, TKBProcessingStatus } from '@/@types/knowledgeBase';

// ─── Status Badge ─────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<TKBProcessingStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  completed: 'Ready',
  failed: 'Failed',
};

const STATUS_VARIANTS: Record<
  TKBProcessingStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  pending: 'outline',
  processing: 'secondary',
  completed: 'default',
  failed: 'destructive',
};

function StatusBadge({ status }: { status: TKBProcessingStatus }) {
  return (
    <Badge variant={STATUS_VARIANTS[status]} className="text-xs capitalize">
      {STATUS_LABELS[status]}
    </Badge>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function KnowledgeBaseTextsTab({
  documents,
  isLoading,
  onSaveText,
  onDelete,
  onToggleShared,
}: IKBTextsTabProps) {
  const {
    title,
    content,
    isShared,
    isSaving,
    selectedIds,
    setTitle,
    setContent,
    setIsShared,
    setIsSaving,
    toggleSelect,
    selectAll,
    resetForm,
  } = useTextEntryForm();

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  // LOCAL_TYPE_JUSTIFIED: simple boolean state, no domain interface needed
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const canSave = title.trim().length > 0 && content.trim().length > 0;

  const handleSave = useCallback(async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSaveText({
        title: title.trim(),
        content: content.trim(),
        isSharedWithCompanies: isShared,
      });
      resetForm();
    } finally {
      setIsSaving(false);
    }
  }, [canSave, isSaving, title, content, isShared, onSaveText, resetForm, setIsSaving]);

  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setIsBulkDeleting(true);
    try {
      await onDelete([...selectedIds]);
    } finally {
      setIsBulkDeleting(false);
    }
  }, [selectedIds, onDelete]);

  const docIds = documents.map(d => d._id);
  const allSelected = docIds.length > 0 && docIds.every(id => selectedIds.has(id));

  return (
    <div className="flex flex-col gap-6">
      {/* Inline form */}
      <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
        <p className="text-sm font-medium text-foreground">Add Text Entry</p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="text-entry-title" className="text-[13px] font-medium">
            Title <span className="text-destructive">*</span>
          </Label>
          <Input
            id="text-entry-title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Entry title"
            className="h-9"
            disabled={isSaving}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="text-entry-content" className="text-[13px] font-medium">
            Content <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="text-entry-content"
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="Enter the text content for this knowledge base entry..."
            rows={4}
            disabled={isSaving}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="text-entry-shared"
              checked={isShared}
              onCheckedChange={checked => setIsShared(checked === true)}
              disabled={isSaving}
            />
            <Label htmlFor="text-entry-shared" className="text-sm font-normal cursor-pointer">
              Share with all companies
            </Label>
          </div>

          <Button onClick={() => void handleSave()} disabled={!canSave || isSaving} size="sm">
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
            {isSaving ? 'Saving...' : 'Save Entry'}
          </Button>
        </div>
      </div>

      {/* Bulk delete bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
          <span className="text-sm text-foreground">{selectedIds.size} selected</span>
          <Button variant="destructive" size="sm" onClick={() => setIsDeleteConfirmOpen(true)}>
            <Trash2 className="w-4 h-4 mr-1.5" aria-hidden="true" />
            Delete selected
          </Button>
        </div>
      )}

      {/* Entries table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
          Loading entries...
        </div>
      ) : documents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 gap-2 text-center">
          <Type className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium text-foreground">No text entries yet</p>
          <p className="text-xs text-muted-foreground">
            Add your first text entry using the form above.
          </p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => selectAll(docIds)}
                  aria-label="Select all"
                />
              </TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-28">Shared</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-36">Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map(doc => (
              <TableRow key={doc._id} className={cn(selectedIds.has(doc._id) && 'bg-muted/30')}>
                <TableCell>
                  <Checkbox
                    checked={selectedIds.has(doc._id)}
                    onCheckedChange={() => toggleSelect(doc._id)}
                    aria-label={`Select ${doc.title}`}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2 min-w-0">
                    <Type className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                    <span className="text-sm text-foreground truncate">{doc.title}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <Switch
                    checked={doc.isSharedWithCompanies}
                    onCheckedChange={checked => void onToggleShared(doc._id, checked)}
                    aria-label={`Toggle shared for ${doc.title}`}
                  />
                </TableCell>
                <TableCell>
                  <StatusBadge status={doc.processingStatus} />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {new Date(doc.createdAt).toLocaleDateString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog
        open={isDeleteConfirmOpen}
        onOpenChange={open => {
          if (!open) setIsDeleteConfirmOpen(false);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <div className="flex items-center gap-3">
              <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
                <AlertTriangle
                  aria-hidden="true"
                  className="h-5 w-5 text-red-600 dark:text-red-400"
                />
              </div>
              <AlertDialogTitle>
                Delete {selectedIds.size === 1 ? '1 document' : `${selectedIds.size} documents`}?
              </AlertDialogTitle>
            </div>
            <AlertDialogDescription className="pt-2">
              This will permanently remove the selected text entries from your knowledge base and
              the RAG search index. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={isBulkDeleting}
              onClick={() => void handleBulkDelete().then(() => setIsDeleteConfirmOpen(false))}
            >
              {isBulkDeleting ? (
                <>
                  <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                `Delete ${selectedIds.size === 1 ? '1 document' : `${selectedIds.size} documents`}`
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
